import './style.css';
import { luminosityFromMass } from './engines/stellar';
import {
  habitableZone,
  equilibriumTemperatureFromSolarUnits,
  driveEquationN,
  averageCivilizationSeparationLy,
  type DrakeEquationParams,
} from './engines/habitability';

// ============================================================
// 言語切り替え
// ============================================================
const langBtn = document.getElementById('langToggle') as HTMLButtonElement;
let currentLang: 'ja' | 'en' = 'ja';

function applyLang(lang: 'ja' | 'en') {
  currentLang = lang;
  document.documentElement.lang = lang;
  document.querySelectorAll<HTMLElement>('[data-ja]').forEach((el) => {
    el.textContent = lang === 'ja' ? el.getAttribute('data-ja') : el.getAttribute('data-en');
  });
  langBtn.querySelectorAll<HTMLElement>('.opt').forEach((opt) => {
    opt.classList.toggle('on', opt.getAttribute('data-lang-opt') === lang);
  });
  const footerMessageJa = document.getElementById('footerMessageJa');
  const footerMessageEn = document.getElementById('footerMessageEn');
  if (footerMessageJa && footerMessageEn) {
    (footerMessageJa as HTMLElement).style.display = lang === 'ja' ? 'block' : 'none';
    (footerMessageEn as HTMLElement).style.display = lang === 'en' ? 'block' : 'none';
  }
}
langBtn.addEventListener('click', () => applyLang(currentLang === 'ja' ? 'en' : 'ja'));

// ============================================================
// オンボーディング
// ============================================================
const ONBOARDING_KEY = 'cosmos-simulator:onboarding-seen-fermi';
const onboarding = document.getElementById('onboarding') as HTMLElement;
const onboardingDismiss = document.getElementById('onboardingDismiss') as HTMLButtonElement;
const helpBtn = document.getElementById('helpBtn') as HTMLButtonElement;

function showOnboarding() {
  onboarding.classList.remove('hidden');
}
function hideOnboarding() {
  onboarding.classList.add('hidden');
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* noop */
  }
}
onboardingDismiss.addEventListener('click', hideOnboarding);
helpBtn.addEventListener('click', showOnboarding);
try {
  if (localStorage.getItem(ONBOARDING_KEY) === '1') onboarding.classList.add('hidden');
} catch {
  /* noop */
}

// ============================================================
// スライダー塗りつぶし同期
// ============================================================
function syncSliderFill(input: HTMLInputElement) {
  const min = parseFloat(input.min || '0');
  const max = parseFloat(input.max || '100');
  const val = parseFloat(input.value);
  const percent = ((val - min) / (max - min)) * 100;
  input.style.setProperty('--range-progress', `${percent}%`);
}

// ============================================================
// 居住可能性計算機
// ============================================================
const hzMassSlider = document.getElementById('hzMassSlider') as HTMLInputElement;
const hzMassVal = document.getElementById('hzMassVal') as HTMLElement;
const distSlider = document.getElementById('distSlider') as HTMLInputElement;
const distVal = document.getElementById('distVal') as HTMLElement;
const albedoSlider = document.getElementById('albedoSlider') as HTMLInputElement;
const albedoVal = document.getElementById('albedoVal') as HTMLElement;
const hzVerdict = document.getElementById('hzVerdict') as HTMLElement;
const hzLumVal = document.getElementById('hzLumVal') as HTMLElement;
const hzInnerVal = document.getElementById('hzInnerVal') as HTMLElement;
const hzOuterVal = document.getElementById('hzOuterVal') as HTMLElement;
const tempVal = document.getElementById('tempVal') as HTMLElement;
const hzValidationNote = document.getElementById('hzValidationNote') as HTMLElement;

interface PlanetPreset {
  massSolar: number;
  distanceAU: number;
  albedo: number;
  realTempK: number;
  nameJa: string;
  nameEn: string;
}
const PLANET_PRESETS: Record<string, PlanetPreset> = {
  earth: { massSolar: 1.0, distanceAU: 1.0, albedo: 0.3, realTempK: 255, nameJa: '地球', nameEn: 'Earth' },
  venus: { massSolar: 1.0, distanceAU: 0.723, albedo: 0.75, realTempK: 232, nameJa: '金星', nameEn: 'Venus' },
  mars: { massSolar: 1.0, distanceAU: 1.524, albedo: 0.25, realTempK: 210, nameJa: '火星', nameEn: 'Mars' },
};

document.querySelectorAll<HTMLButtonElement>('.fermi-panel:first-child .preset-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const key = btn.getAttribute('data-preset');
    if (!key || !(key in PLANET_PRESETS)) return;
    const preset = PLANET_PRESETS[key];
    hzMassSlider.value = String(preset.massSolar);
    distSlider.value = String(preset.distanceAU);
    albedoSlider.value = String(preset.albedo);
    document.querySelectorAll('.fermi-panel:first-child .preset-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    updateHabitability(preset);
  });
});

function updateHabitability(activePreset?: PlanetPreset) {
  const massSolar = parseFloat(hzMassSlider.value);
  const distanceAU = parseFloat(distSlider.value);
  const albedo = parseFloat(albedoSlider.value);

  hzMassVal.textContent = massSolar.toFixed(2);
  distVal.textContent = distanceAU.toFixed(2);
  albedoVal.textContent = albedo.toFixed(2);
  syncSliderFill(hzMassSlider);
  syncSliderFill(distSlider);
  syncSliderFill(albedoSlider);

  const luminositySolar = luminosityFromMass(massSolar);
  const hz = habitableZone(luminositySolar);
  const tEq = equilibriumTemperatureFromSolarUnits(luminositySolar, distanceAU, albedo);
  const inHZ = distanceAU >= hz.innerAU && distanceAU <= hz.outerAU;

  hzLumVal.textContent = `${luminositySolar < 100 ? luminositySolar.toFixed(2) : luminositySolar.toExponential(2)} L☉`;
  hzInnerVal.textContent = `${hz.innerAU.toFixed(2)} AU`;
  hzOuterVal.textContent = `${hz.outerAU.toFixed(2)} AU`;
  tempVal.textContent = `${tEq.toFixed(0)} K`;

  hzVerdict.classList.toggle('in-hz', inHZ);
  hzVerdict.classList.toggle('out-hz', !inHZ);
  const verdictJa = inHZ ? '✓ ハビタブルゾーン内' : '✕ ハビタブルゾーン外';
  const verdictEn = inHZ ? '✓ Inside Habitable Zone' : '✕ Outside Habitable Zone';
  hzVerdict.setAttribute('data-ja', verdictJa);
  hzVerdict.setAttribute('data-en', verdictEn);
  hzVerdict.textContent = currentLang === 'ja' ? verdictJa : verdictEn;

  if (activePreset) {
    const err = Math.abs((tEq - activePreset.realTempK) / activePreset.realTempK) * 100;
    const noteJa = `${activePreset.nameJa}実測: 平衡温度${activePreset.realTempK}K付近との誤差${err.toFixed(1)}%`;
    const noteEn = `${activePreset.nameEn} reference: ~${err.toFixed(1)}% from measured equilibrium ${activePreset.realTempK}K`;
    hzValidationNote.setAttribute('data-ja', noteJa);
    hzValidationNote.setAttribute('data-en', noteEn);
    hzValidationNote.textContent = currentLang === 'ja' ? noteJa : noteEn;
  } else {
    const noteJa = '地球実測: 平衡温度255K(実際の地表288Kとの差33Kが温室効果)';
    const noteEn = 'Earth reference: equilibrium 255K (33K below actual 288K = greenhouse effect)';
    hzValidationNote.setAttribute('data-ja', noteJa);
    hzValidationNote.setAttribute('data-en', noteEn);
    hzValidationNote.textContent = currentLang === 'ja' ? noteJa : noteEn;
  }
}

hzMassSlider.addEventListener('input', () => {
  document.querySelectorAll('.fermi-panel:first-child .preset-btn').forEach((b) => b.classList.remove('active'));
  updateHabitability();
});
distSlider.addEventListener('input', () => {
  document.querySelectorAll('.fermi-panel:first-child .preset-btn').forEach((b) => b.classList.remove('active'));
  updateHabitability();
});
albedoSlider.addEventListener('input', () => {
  document.querySelectorAll('.fermi-panel:first-child .preset-btn').forEach((b) => b.classList.remove('active'));
  updateHabitability();
});

// ============================================================
// ドレイクの方程式
// ============================================================
const rStarSlider = document.getElementById('rStarSlider') as HTMLInputElement;
const rStarVal = document.getElementById('rStarVal') as HTMLElement;
const fpSlider = document.getElementById('fpSlider') as HTMLInputElement;
const fpVal = document.getElementById('fpVal') as HTMLElement;
const neSlider = document.getElementById('neSlider') as HTMLInputElement;
const neVal = document.getElementById('neVal') as HTMLElement;
const flSlider = document.getElementById('flSlider') as HTMLInputElement;
const flVal = document.getElementById('flVal') as HTMLElement;
const fiSlider = document.getElementById('fiSlider') as HTMLInputElement;
const fiVal = document.getElementById('fiVal') as HTMLElement;
const fcSlider = document.getElementById('fcSlider') as HTMLInputElement;
const fcVal = document.getElementById('fcVal') as HTMLElement;
const lSlider = document.getElementById('lSlider') as HTMLInputElement;
const lVal = document.getElementById('lVal') as HTMLElement;
const nValue = document.getElementById('nValue') as HTMLElement;
const sepValue = document.getElementById('sepValue') as HTMLElement;

// fi, fc, L は対数スケール（現実的な不確実性の桁数をスライダーで表現するため）
function logSliderToValue(sliderVal: number, sliderMax: number, minExp: number, maxExp: number): number {
  const exp = minExp + (sliderVal / sliderMax) * (maxExp - minExp);
  return Math.pow(10, exp);
}
function valueToLogSlider(value: number, sliderMax: number, minExp: number, maxExp: number): number {
  const exp = Math.log10(value);
  return ((exp - minExp) / (maxExp - minExp)) * sliderMax;
}

const FI_FC_MIN_EXP = -10;
const FI_FC_MAX_EXP = 0;
const L_MIN_EXP = 0;
const L_MAX_EXP = 9;

interface DrakePreset {
  rStar: number;
  fp: number;
  ne: number;
  fl: number;
  fi: number;
  fc: number;
  lYears: number;
}
const DRAKE_PRESETS: Record<string, DrakePreset> = {
  optimistic: { rStar: 20, fp: 1.0, ne: 5, fl: 1.0, fi: 1.0, fc: 1.0, lYears: 1e8 },
  moderate: { rStar: 7, fp: 1.0, ne: 2, fl: 1.0, fi: 0.01, fc: 0.01, lYears: 10000 },
  pessimistic: { rStar: 1.5, fp: 0.5, ne: 0.1, fl: 0.01, fi: 1e-6, fc: 1e-3, lYears: 200 },
};

document.querySelectorAll<HTMLButtonElement>('.fermi-panel:nth-child(2) .preset-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const key = btn.getAttribute('data-preset');
    if (!key || !(key in DRAKE_PRESETS)) return;
    const p = DRAKE_PRESETS[key];
    rStarSlider.value = String(p.rStar);
    fpSlider.value = String(p.fp * 100);
    neSlider.value = String(p.ne * 100);
    flSlider.value = String(p.fl * 100);
    fiSlider.value = String(valueToLogSlider(p.fi, 1000, FI_FC_MIN_EXP, FI_FC_MAX_EXP));
    fcSlider.value = String(valueToLogSlider(p.fc, 1000, FI_FC_MIN_EXP, FI_FC_MAX_EXP));
    lSlider.value = String(valueToLogSlider(p.lYears, 700, L_MIN_EXP, L_MAX_EXP));
    document.querySelectorAll('.fermi-panel:nth-child(2) .preset-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    updateDrake();
  });
});

function formatScientific(n: number): string {
  if (n === 0) return '0';
  if (n >= 0.01 && n < 1e6) {
    return n < 1 ? n.toFixed(4) : n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
  return n.toExponential(2);
}

function updateDrake() {
  const rStar = parseFloat(rStarSlider.value);
  const fp = parseFloat(fpSlider.value) / 100;
  const ne = parseFloat(neSlider.value) / 100;
  const fl = parseFloat(flSlider.value) / 100;
  const fi = logSliderToValue(parseFloat(fiSlider.value), 1000, FI_FC_MIN_EXP, FI_FC_MAX_EXP);
  const fc = logSliderToValue(parseFloat(fcSlider.value), 1000, FI_FC_MIN_EXP, FI_FC_MAX_EXP);
  const lYears = logSliderToValue(parseFloat(lSlider.value), 700, L_MIN_EXP, L_MAX_EXP);

  rStarVal.textContent = rStar.toFixed(1);
  fpVal.textContent = fp.toFixed(2);
  neVal.textContent = ne.toFixed(2);
  flVal.textContent = fl.toFixed(2);
  fiVal.textContent = formatScientific(fi);
  fcVal.textContent = formatScientific(fc);
  lVal.textContent = lYears < 1e6 ? Math.round(lYears).toLocaleString() : lYears.toExponential(2);

  [rStarSlider, fpSlider, neSlider, flSlider, fiSlider, fcSlider, lSlider].forEach(syncSliderFill);

  const params: DrakeEquationParams = {
    starFormationRate: rStar,
    fractionWithPlanets: fp,
    habitablePlanetsPerSystem: ne,
    fractionDevelopingLife: fl,
    fractionDevelopingIntelligence: fi,
    fractionDetectable: fc,
    civilizationLifetimeYears: lYears,
  };
  const n = driveEquationN(params);
  nValue.textContent = formatScientific(n);

  const sep = averageCivilizationSeparationLy(Math.max(n, 1e-9));
  const sepJa = n < 1 ? `平均距離: 概念上の値(N<1)` : `平均距離: 約${formatScientific(sep)} 光年`;
  const sepEn = n < 1 ? `Avg. separation: not meaningful (N<1)` : `Avg. separation: ≈${formatScientific(sep)} ly`;
  sepValue.setAttribute('data-ja', sepJa);
  sepValue.setAttribute('data-en', sepEn);
  sepValue.textContent = currentLang === 'ja' ? sepJa : sepEn;

  renderGalaxy(n);
}

[rStarSlider, fpSlider, neSlider, flSlider, fiSlider, fcSlider, lSlider].forEach((el) => {
  el.addEventListener('input', () => {
    document.querySelectorAll('.fermi-panel:nth-child(2) .preset-btn').forEach((b) => b.classList.remove('active'));
    updateDrake();
  });
});

// ============================================================
// 銀河マップ
// ============================================================
const galaxyWrap = document.getElementById('galaxyWrap') as HTMLElement;
const MAX_RENDERED_DOTS = 300;
let galaxyDotPool: HTMLElement[] = [];

function renderGalaxy(n: number) {
  const dotsToShow = Math.min(Math.round(n), MAX_RENDERED_DOTS);

  // 足りない分のプールを追加生成
  while (galaxyDotPool.length < dotsToShow) {
    const dot = document.createElement('div');
    dot.className = 'galaxy-dot';
    galaxyWrap.appendChild(dot);
    galaxyDotPool.push(dot);
  }

  const rect = galaxyWrap.getBoundingClientRect();
  const cx = rect.width / 2;
  const cy = rect.height / 2;
  const maxR = Math.min(cx, cy) - 10;

  galaxyDotPool.forEach((dot, i) => {
    if (i < dotsToShow) {
      // シード付き疑似ランダム（毎フレーム位置が変わらないよう、インデックスから決定論的に生成）
      const angle = (i * 137.5077) % 360; // 黄金角で均等に分布
      const radiusFrac = Math.sqrt(((i * 9973) % 1000) / 1000); // 円盤上に一様分布
      const r = radiusFrac * maxR;
      const x = cx + r * Math.cos((angle * Math.PI) / 180);
      const y = cy + r * Math.sin((angle * Math.PI) / 180);
      dot.style.left = `${x}px`;
      dot.style.top = `${y}px`;
      dot.style.display = 'block';
    } else {
      dot.style.display = 'none';
    }
  });
}

// ============================================================
// 初期化
// ============================================================
updateHabitability(PLANET_PRESETS.earth);
updateDrake();
window.addEventListener('resize', () => {
  const rStar = parseFloat(rStarSlider.value);
  const fp = parseFloat(fpSlider.value) / 100;
  const ne = parseFloat(neSlider.value) / 100;
  const fl = parseFloat(flSlider.value) / 100;
  const fi = logSliderToValue(parseFloat(fiSlider.value), 1000, FI_FC_MIN_EXP, FI_FC_MAX_EXP);
  const fc = logSliderToValue(parseFloat(fcSlider.value), 1000, FI_FC_MIN_EXP, FI_FC_MAX_EXP);
  const lYears = logSliderToValue(parseFloat(lSlider.value), 700, L_MIN_EXP, L_MAX_EXP);
  const n = driveEquationN({
    starFormationRate: rStar,
    fractionWithPlanets: fp,
    habitablePlanetsPerSystem: ne,
    fractionDevelopingLife: fl,
    fractionDevelopingIntelligence: fi,
    fractionDetectable: fc,
    civilizationLifetimeYears: lYears,
  });
  renderGalaxy(n);
});

// ページローダーを隠す
const pageLoader = document.getElementById('pageLoader');
if (pageLoader) {
  requestAnimationFrame(() => {
    pageLoader.classList.add('hide');
    setTimeout(() => pageLoader.remove(), 400);
  });
}

// ============================================================
// フッターメッセージ（クリックで開閉）
// ============================================================
const dedicationFooter = document.getElementById('dedicationFooter');
const footerMessageOverlay = document.getElementById('footerMessageOverlay');
const footerMessageClose = document.getElementById('footerMessageClose');
if (dedicationFooter && footerMessageOverlay && footerMessageClose) {
  dedicationFooter.addEventListener('click', () => {
    footerMessageOverlay.classList.add('show');
  });
  footerMessageClose.addEventListener('click', () => {
    footerMessageOverlay.classList.remove('show');
  });
  footerMessageOverlay.addEventListener('click', (e) => {
    if (e.target === footerMessageOverlay) footerMessageOverlay.classList.remove('show');
  });
}
